// Recipient input + send. Defaults the "to" field to the patient's
// email when one is on file, so the most common path ("send this to
// the patient") needs zero typing.

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { emailDocument } from '../../../api/documents.js'
import Modal, { Field, ModalActions, PrimaryButton, SecondaryButton } from './Modal.jsx'

export default function EmailDocumentModal({ doc, encounter, onClose, onSent }) {
  const defaultTo = encounter?.patient_email || ''
  const [to, setTo] = useState(defaultTo)
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [sentSummary, setSentSummary] = useState(null)

  const send = useMutation({
    mutationFn: () => emailDocument({
      id: doc.id,
      to: to || undefined,           // omit so backend falls back to patient email
      cc: cc.split(',').map((s) => s.trim()).filter(Boolean),
      subject: subject || undefined,
      message: message || undefined,
    }),
    onSuccess: (data) => {
      setSentSummary(data)
      onSent?.(data)
    },
  })

  if (!doc) return null

  return (
    <Modal
      title="Email document"
      subtitle="Send the PDF as an attachment."
      onClose={onClose}
    >
      {sentSummary ? (
        <div>
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-4 mb-4">
            <div className="font-bold text-emerald-900">Sent.</div>
            <div className="text-sm text-emerald-800 mt-1">
              Delivered to {sentSummary.sent_to.join(', ')}
              {sentSummary.cc?.length > 0 && <> · cc {sentSummary.cc.join(', ')}</>}
            </div>
          </div>
          <ModalActions>
            <PrimaryButton onClick={onClose}>Done</PrimaryButton>
          </ModalActions>
        </div>
      ) : (
        <div>
          <Field
            label="To"
            hint={defaultTo
              ? `Patient email pre-filled: ${defaultTo}`
              : "No patient email on file — please enter a recipient."}
          >
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-base w-full"
              placeholder="recipient@example.com"
              autoFocus={!defaultTo}
            />
          </Field>
          <Field label="Cc (optional)">
            <input
              type="text"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
              placeholder="other@example.com, third@example.com"
            />
          </Field>
          <Field label="Subject (optional)" hint="Defaults to document type + patient name">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
              placeholder="(default subject)"
            />
          </Field>
          <Field label="Message (optional)" hint="A standard cover note is used if blank">
            <textarea
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="border-2 border-slate-200 focus:border-wantok-600 focus:outline-none rounded-lg px-3 py-2 text-sm w-full"
              placeholder="(default message)"
            />
          </Field>

          {send.isError && (
            <div className="text-xs bg-rose-50 border-2 border-rose-200 text-rose-800 rounded-lg px-3 py-2 mb-3 font-medium">
              {send.error?.response?.data?.detail
                || send.error?.response?.data?.to
                || send.error?.message
                || 'Failed to send.'}
            </div>
          )}

          <ModalActions>
            <SecondaryButton onClick={onClose} disabled={send.isPending}>Cancel</SecondaryButton>
            <PrimaryButton
              onClick={() => send.mutate()}
              disabled={send.isPending || (!to && !defaultTo)}
            >
              {send.isPending ? 'Sending…' : 'Send email'}
            </PrimaryButton>
          </ModalActions>
        </div>
      )}
    </Modal>
  )
}
